import { Formik } from "formik";
import React from "react";
import { Card, Form } from "react-bootstrap";
import {Button, IconButton} from '@blueprint/ui';
import styled from "styled-components";

import { NoRecordCard, ProjectAvatar } from "../../../common/components";
import { Table } from "../../../common/components/core";
import { InlineInputField } from "../../../common/components/form";
import { SWIFTPattern } from "../../../misc/validators";
import { BesuProjectState } from "../../../types/project";
import { getNameInitial } from "../../../utils/common";
import { withBankAccountNumberInput } from "../../../common/components/form/HOC/withBankAccountNumberInput";

export type ProjectAccountFormValues = {
    [key: string]: string[];
};

const validate = (values: ProjectAccountFormValues) => {
    const allAccounts = Object.values(values)
        .flat()
        .filter((account) => !!account);
    const errors: { [key: string]: (string | undefined)[] } = {};
    Object.keys(values).forEach((key) => {
        const errorList =
            values[key]?.map((account) => {
                if (
                    account &&
                    allAccounts.filter((it) => it === account).length > 1
                ) {
                    return "Duplicated Bank Account Number Found";
                } else if (account && !account.match(SWIFTPattern)) {
                    return "Invalid Bank Account Number";
                } else {
                    return undefined;
                }
            }) ?? [];
        if (errorList.some((it) => it !== undefined)) {
            errors[key] = errorList;
        }
    });

    return errors;
};

const normaliseRefs = (refs?: string[] | null) => {
    // 视业务情况：是否需要 trim / 忽略空字符串
    const cleaned = (refs ?? []).map(s => s?.trim()).filter((s): s is string => !!s);
    // 去重 + 排序，确保比较稳定（不受顺序影响）
    return Array.from(new Set(cleaned)).sort();
};

const arraysEqual = (a: string[], b: string[]) => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false;
    }
    return true;
};

type ProjectAccountFormProps = {
    projects: BesuProjectState[];
    onCancel: () => void;
    onSubmit: (values: ProjectAccountFormValues) => void;
};

const BankAccountNumberInputField = withBankAccountNumberInput(InlineInputField);

const ProjectAccountForm = ({
    projects,
    onSubmit,
    onCancel,
}: ProjectAccountFormProps): JSX.Element => {
    const sortedProjects = [...projects].sort((a, b) =>
        a.name?.localeCompare(b.name),
    );

    const getChangedProjectAccountValues = (
        projects: BesuProjectState[],
        values: ProjectAccountFormValues
    ): ProjectAccountFormValues => {
        const originalMap = new Map<string, string[]>();
        for (const p of projects) {
            originalMap.set(String(p.referenceId), normaliseRefs(p.bankAccountRefs));
        }
        const changed: ProjectAccountFormValues = {};

        for (const [referenceId, newRefs] of Object.entries(values)) {
            const originalRefs = originalMap.get(referenceId) ?? normaliseRefs([]);
            const nextRefs = normaliseRefs(newRefs);

            if (!arraysEqual(originalRefs, nextRefs)) {
                changed[referenceId] = nextRefs;
            }
        }
        return changed;
    };

    const submitChanged = async (values: ProjectAccountFormValues) => {
        const changedValues = getChangedProjectAccountValues(projects, values);
        onSubmit(changedValues);
    }
    return (
        <Formik
            validate={validate}
            validateOnBlur={false}
            validateOnChange={false}
            onSubmit={submitChanged}
            initialValues={Object.fromEntries(
                sortedProjects.map((project) => [
                    project.referenceId,
                    project.bankAccountRefs.length === 0
                        ? [""]
                        : project.bankAccountRefs,
                ]),
            )}
        >
            {({ handleSubmit, values, setValues }) => (
                <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                    <div className="d-flex align-items-center my-3">
                        <h1 className="flex-grow-1 mb-0">
                            Manage Project Bank Account{" "}
                        </h1>
                        <Button
                            variant="light.tertiary"
                            className="ms-4"
                            onClick={onCancel}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="light.primary"
                            className="ms-4"
                            type="submit"
                        >
                            Update
                        </Button>
                    </div>
                    {sortedProjects.length === 0 ? (
                        <NoRecordCard />
                    ) : (
                        <Card className="mt-3 border-0">
                            <Card.Body className="p-0">
                                <table className={"custom-table"}>
                                    <thead>
                                        <tr>
                                            <Table.HeaderCell width={200}>
                                                Project Name
                                            </Table.HeaderCell>
                                            <Table.HeaderCell width={100}>
                                                Project ID
                                            </Table.HeaderCell>
                                            <Table.HeaderCell width={40}>
                                                Add/Delete
                                            </Table.HeaderCell>
                                            <Table.HeaderCell width={550}>
                                                Bank Account Number
                                            </Table.HeaderCell>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedProjects.map((project) => {
                                            const row =
                                                values[project.referenceId] ?? [];
                                            return row?.map(
                                                (account, accountIndex) => (
                                                    <Table.BodyRow
                                                        key={`${project.externalProjectId}_${accountIndex}`}
                                                        plain
                                                    >
                                                        <td>
                                                            {accountIndex ===
                                                                0 && (
                                                                    <div className="d-flex align-items-center">
                                                                        <ProjectAvatar variant="small">
                                                                            {getNameInitial(project.name)}
                                                                        </ProjectAvatar>
                                                                        <div className="flex-grow-1 mb-0 ms-3">{project.name}</div>
                                                                    </div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {accountIndex ===
                                                                0 &&
                                                                project.externalProjectId}
                                                        </td>
                                                        <td>
                                                            {accountIndex ===
                                                            0 ? (
                                                                    <IconButton
                                                                        aria-label={`add bank account to ${project.name}`}
                                                                        icon="Add"
                                                                        variant="light.tertiary"
                                                                        size="small"
                                                                        onClick={() =>
                                                                            setValues(
                                                                                {
                                                                                    ...values,
                                                                                    [project
                                                                                        .referenceId]:
                                                                                        row.concat(
                                                                                            "",
                                                                                        ),
                                                                                },
                                                                            )
                                                                        }
                                                                    />
                                                            ) : (
                                                                <IconButton
                                                                    aria-label={`delete bank account from ${project.name}`}
                                                                    icon="Delete"
                                                                    variant="light.tertiary"
                                                                    size="small"
                                                                    onClick={() =>
                                                                        setValues(
                                                                            {
                                                                                ...values,
                                                                                [project
                                                                                    .referenceId]:
                                                                                    [
                                                                                        ...row.slice(
                                                                                            0,
                                                                                            accountIndex,
                                                                                        ),
                                                                                        ...row.slice(
                                                                                            accountIndex +
                                                                                            1,
                                                                                        ),
                                                                                    ],
                                                                            },
                                                                        )
                                                                    }
                                                                />
                                                            )}
                                                        </td>
                                                        <td>
                                                            <StyledInlineInputBankAccountNumberField
                                                                type="text"
                                                                name={`${project.referenceId}[${accountIndex}]`}
                                                                maxLength={18}
                                                                className={"input-group small"}
                                                            />
                                                        </td>
                                                    </Table.BodyRow>
                                                ),
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </Card.Body>
                        </Card>
                    )}
                </Form>
            )}
        </Formik>
    );
};

const StyledInlineInputBankAccountNumberField = styled(
    BankAccountNumberInputField,
)`
    input {
        max-width: 200px;
    }
`;

export default ProjectAccountForm;
